import { useEffect, useMemo, useRef, useState } from 'react';
import { FormControl, InputLabel, MenuItem, Select } from '@material-ui/core';
import {
  Content,
  Header,
  Page,
  Progress,
  Table,
  TableColumn,
  WarningPanel,
} from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef, EntityRefLink } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import { evaluate, snykRepo, verdict } from '../health/health';

const ENVIRONMENTS = ['dev', 'qa', 'prod'] as const;
const SNYK_ORG = '1472874a-a3e7-47bd-8368-c203eba7c579';
const SONAR_KEY = 'sonarqube.org/project-key';
const K8S_NS = 'backstage.io/kubernetes-namespace';

type Row = {
  entity: Entity;
  name: string;
  health: string;
  sonar: string;
  coverage: string;
  snyk: string;
  dev: string;
  qa: string;
  prod: string;
};

const columns: TableColumn<Row>[] = [
  { title: 'Servicio', field: 'name', render: row => <EntityRefLink entityRef={row.entity} defaultKind="component" /> },
  { title: 'Salud', field: 'health' },
  { title: 'Sonar', field: 'sonar' },
  { title: 'Cobertura', field: 'coverage' },
  { title: 'Snyk (crít./altas)', field: 'snyk' },
  { title: 'dev', field: 'dev' },
  { title: 'qa', field: 'qa' },
  { title: 'prod', field: 'prod' },
];

const GATE: Record<string, string> = { OK: 'Aprobado', ERROR: 'Rechazado' };
const HEALTH = { pass: '✔ Aprobado', fail: '✖ No pasa', unknown: '- Sin datos' };

const ownerOf = (e: Entity) => String(e.spec?.owner ?? '').replace(/^group:(default\/)?/, '');

/** Tablero por ECO: calidad (Sonar), vulnerabilidades (Snyk) y versión en cada ambiente de sus microservicios. */
export const EcoDashboardPage = () => {
  const catalogApi = useApi(catalogApiRef);
  const discovery = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);
  const [components, setComponents] = useState<Entity[]>();
  const [eco, setEco] = useState('');
  const [rows, setRows] = useState<Row[]>();
  const [error, setError] = useState<string>();
  // Snyk se lee una vez por visita (todas las páginas del org), no en cada cambio de ECO.
  const snykCounts = useRef<Promise<Map<string, number[]>>>();

  useEffect(() => {
    catalogApi
      .getEntities({
        filter: { kind: 'Component', 'spec.type': 'microservice' },
        fields: ['kind', 'metadata.name', 'metadata.namespace', 'metadata.annotations', 'spec.owner'],
      })
      .then(r => setComponents(r.items))
      .catch(e => setError(String(e)));
  }, [catalogApi]);

  const ecos = useMemo(() => [...new Set((components ?? []).map(ownerOf))].sort(), [components]);

  useEffect(() => {
    if (!eco || !components) return;
    const mine = components.filter(c => ownerOf(c) === eco);
    setRows(undefined);
    (async () => {
      const [proxy, sonarBase, k8sBase] = await Promise.all(
        ['proxy', 'sonarqube', 'kubernetes'].map(id => discovery.getBaseUrl(id)),
      );
      const json = async (url: string, init?: RequestInit) => {
        const res = await fetch(url, init);
        if (!res.ok) throw new Error(`${res.status}`);
        return res.json();
      };

      // Versión: deployments de cada namespace del ECO en cada clúster (pocas llamadas, no una por servicio).
      const namespaces = [...new Set(mine.map(c => c.metadata.annotations?.[K8S_NS]).filter(Boolean))] as string[];
      const versions = new Map<string, string>(); // `${env}/${servicio}` -> tag
      await Promise.all(
        ENVIRONMENTS.flatMap(env =>
          namespaces.map(ns =>
            // ?cluster=: CloudFront no incluye la cabecera en su caché; sin esto los 3 clústeres devuelven dev.
            json(`${k8sBase}/proxy/apis/apps/v1/namespaces/${ns}/deployments?cluster=${env}`, {
              headers: { 'Backstage-Kubernetes-Cluster': env },
            })
              .then((list: any) => {
                for (const d of list.items ?? []) {
                  const containers = (d.spec?.template?.spec?.containers ?? []).filter((c: any) => c.name !== 'istio-proxy');
                  const image: string = (containers.find((c: any) => c.name === d.metadata.name) ?? containers[0])?.image ?? '';
                  const off = (d.spec?.replicas ?? 0) === 0 ? ' (apagado)' : '';
                  versions.set(`${env}/${d.metadata.name}`, `${image.split(':').pop()}${off}`);
                }
              })
              .catch(() => undefined), // sin acceso a un clúster: esa columna queda en "-"
          ),
        ),
      );

      // Snyk: proyectos del org con el conteo de issues más reciente; se suman por repo.
      snykCounts.current ??= (async () => {
        const counts = new Map<string, number[]>();
        let next: string | undefined = `/rest/orgs/${SNYK_ORG}/projects?version=2024-06-21&limit=100&meta.latest_issue_counts=true`;
        while (next) {
          const page: any = await json(`${proxy}/snyk${next}`).catch(() => undefined);
          for (const p of page?.data ?? []) {
            const repo = snykRepo(String(p.attributes?.name ?? ''));
            const issues = p.meta?.latest_issue_counts ?? {};
            const [c, h] = counts.get(repo) ?? [0, 0];
            counts.set(repo, [c + (issues.critical ?? 0), h + (issues.high ?? 0)]);
          }
          next = page?.links?.next;
        }
        return counts;
      })();
      const snyk = await snykCounts.current;

      const result = await Promise.all(
        mine.map(async entity => {
          const name = entity.metadata.name;
          const key = entity.metadata.annotations?.[SONAR_KEY];
          const findings: any = key
            ? await json(`${sonarBase}/findings?componentKey=${encodeURIComponent(key)}`).catch(() => undefined)
            : undefined;
          const measure = (metric: string) => findings?.measures?.find((m: any) => m.metric === metric)?.value;
          const gate = measure('alert_status');
          const coverage = measure('coverage');
          const vulns = snyk.get(name);
          return {
            entity,
            name,
            health: HEALTH[verdict(evaluate(findings?.measures, vulns as [number, number] | undefined))],
            sonar: GATE[gate] ?? '-',
            coverage: coverage ? `${coverage}%` : '-',
            snyk: vulns ? `${vulns[0]} / ${vulns[1]}` : '-',
            ...Object.fromEntries(ENVIRONMENTS.map(env => [env, versions.get(`${env}/${name}`) ?? '-'])),
          } as Row;
        }),
      );
      setRows(result);
    })().catch(e => setError(String(e?.message ?? e)));
  }, [eco, components, discovery, fetch]);

  return (
    <Page themeId="tool">
      <Header title="Tablero por ECO" subtitle="Calidad, vulnerabilidades y versión por ambiente de los microservicios de cada equipo" />
      <Content>
        {error && <WarningPanel title="No se pudo cargar el tablero" message={error} />}
        {!components && !error && <Progress />}
        {components && (
          <FormControl variant="outlined" size="small" style={{ minWidth: 300, marginBottom: 16 }}>
            <InputLabel>ECO</InputLabel>
            <Select value={eco} label="ECO" onChange={e => setEco(e.target.value as string)}>
              {ecos.map(name => (
                <MenuItem key={name} value={name}>
                  {name} ({components.filter(c => ownerOf(c) === name).length})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {eco && !rows && !error && <Progress />}
        {rows && (
          <Table<Row>
            title={`${eco}: ${rows.length} microservicios`}
            options={{ paging: false, search: true, padding: 'dense' }}
            columns={columns}
            data={rows}
          />
        )}
      </Content>
    </Page>
  );
};
